/**
 * PatientFormDialog — alta y edición de pacientes.
 *
 * Extraído de PatientsPage (2026-08-25): la ficha general del paciente ahora
 * tiene su propio botón «Editar» y usa este mismo formulario, sin obligar a
 * volver al listado de pacientes para cambiar un teléfono.
 *
 * Es el ÚNICO formulario de alta de contacto: el diálogo de nueva cita y el
 * resumen de la cita lo abren desde dentro (administración, 2026-09-11) en vez de
 * llevar cada uno su copia de los campos, que es como se acababa pudiendo
 * crear un contacto sin apellidos por una pantalla y no por otra.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import CampoComoNosConocio from './CampoComoNosConocio';
import { AVISO_FALTA_ORIGEN, faltaOrigen } from '../lib/origenes';
import { OPCIONES_SEXO, sexoCanonico } from '../lib/sexo';

/**
 * Los cuatro datos sin los que no se da de alta a nadie (administración,
 * 2026-09-11: «nombre, dos apellidos y teléfono»). Las etiquetas son las
 * mismas que devuelve el servidor en su 400, para que el aviso de la
 * pantalla y el del servidor digan lo mismo.
 */
export const CAMPOS_ALTA = [
  ['name', 'Nombre', 'el nombre'],
  ['surname1', 'Primer apellido', 'el primer apellido'],
  ['surname2', 'Segundo apellido', 'el segundo apellido'],
  ['phone', 'Teléfono', 'el teléfono'],
];

/**
 * El NOMBRE es obligatorio siempre, también al editar: una ficha sin nombre
 * no se puede ni buscar, y el servidor propaga el nombre completo a las citas
 * y a las sesiones de quirófano, así que vaciarlo vacía también las agendas.
 * Los dos apellidos y el teléfono solo se exigen al CREAR (hay fichas
 * antiguas sin segundo apellido: no se les inventa uno).
 */
export const SIEMPRE_OBLIGATORIO = ['name'];
const ETIQUETAS_SIEMPRE = CAMPOS_ALTA
  .filter(([clave]) => SIEMPRE_OBLIGATORIO.includes(clave))
  .map(([, , etiqueta]) => etiqueta);

/** El email NO es obligatorio —mucha ficha no tiene correo—, pero si se
 *  escribe tiene que parecer un correo. Mismo criterio que el servidor. */
const EMAIL_RE = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;
export const emailValido = (v) => {
  const t = String(v ?? '').trim();
  return !t || EMAIL_RE.test(t);
};
export const AVISO_EMAIL = 'El email no parece un correo (ejemplo: nombre@dominio.es)';
export const AVISO_NOMBRE_VACIO = 'El nombre no puede quedar vacío';

const vacio = (v) => !String(v ?? '').trim();

/** «el segundo apellido y el teléfono» */
export function enumerar(partes) {
  if (partes.length === 1) return partes[0];
  return `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
}

/**
 * Qué falta para dar de alta un contacto, en cristiano. Se exporta porque hay
 * otras dos pantallas que dan de alta (el alta rápida del presupuesto y la
 * revisión de la LOPD escaneada): la regla se escribe UNA vez y así ninguna
 * avisa distinto —ni tarde— de lo que pide el servidor.
 */
export function avisoAltaIncompleta(form) {
  const faltan = faltanDe(form);
  return faltan.length ? `Para crear el contacto falta ${enumerar(faltan)}` : '';
}

/**
 * Lo que impide dar de alta, «cómo nos conoció» incluido (decisión de
 * Dirección, 23/9/2026: obligatorio al dar de alta). En el MISMO orden que el
 * servidor: primero los cuatro de siempre, después el origen. Aparte de
 * `avisoAltaIncompleta` para no cambiarle a nadie esa frase.
 */
export function avisoAltaConOrigen(form) {
  return avisoAltaIncompleta(form) || (faltaOrigen(form.how_known_us) ? AVISO_FALTA_ORIGEN : '');
}

/**
 * La ficha que abre «Editar», con `how_known_us` SIEMPRE presente (23/9/2026).
 * Hay fichas antiguas que ni tienen la clave: sin esto el formulario no la
 * mandaba y el servidor no podía saber que la ventana «Editar» la dejaba
 * vacía. Con la clave, el servidor impone la regla igual que la pantalla.
 */
const conOrigen = (ficha) => ({ ...ficha, how_known_us: ficha.how_known_us ?? '' });

/**
 * LO QUE SE EDITA EN ESTA VENTANA, y nada más (23/9/2026).
 *
 * La ventana mandaba la ficha ENTERA tal como se cargó al abrir la página, y
 * así borraba en silencio lo que otra persona hubiera guardado mientras tanto:
 * la administrativa corregía un teléfono y la alergia «LATEX» que el doctor
 * acababa de apuntar volvía a «No» (el supervisor lo marcó GRAVE). Lo mismo
 * con el IVA. Desde hoy se manda solo lo que se ha cambiado AQUÍ, de estos
 * campos, con el valor que tenía cada uno al abrir (`valores_al_abrir`): si
 * otra persona cambió ESE mismo campo entre medias, el servidor contesta 409
 * y no pisa nada; si cambió otro, se guardan los dos.
 */
export const CAMPOS_DE_LA_VENTANA = [
  'name', 'surname1', 'surname2', 'nif', 'birth_date', 'sexo', 'phone', 'email',
  'profession', 'how_known_us', 'address',
];

const vacioONada = (v) => v === null || v === undefined || v === '';

/** ¿Es lo mismo? Vacío es vacío; la dirección, casilla a casilla. */
export function mismoValor(a, b) {
  const esObj = (v) => v !== null && typeof v === 'object';
  if (esObj(a) || esObj(b)) {
    const x = esObj(a) ? a : {};
    const y = esObj(b) ? b : {};
    return [...new Set([...Object.keys(x), ...Object.keys(y)])].every((k) => mismoValor(x[k], y[k]));
  }
  if (vacioONada(a) || vacioONada(b)) return vacioONada(a) && vacioONada(b);
  return a === b;
}

/** `{ cambios, valoresAlAbrir }` de la ventana respecto a la ficha abierta. */
export function cambiosDeLaVentana(abierta, form) {
  const cambios = {};
  const valoresAlAbrir = {};
  CAMPOS_DE_LA_VENTANA.forEach((clave) => {
    if (!mismoValor(abierta?.[clave], form[clave])) {
      cambios[clave] = form[clave] ?? '';
      valoresAlAbrir[clave] = abierta?.[clave] ?? null;
    }
  });
  return { cambios, valoresAlAbrir };
}

/**
 * Reparte un nombre suelto («Ana Pérez Gómez», el que lleva una cita que aún
 * no tiene ficha) en nombre y dos apellidos, para no volver a teclearlo al
 * dar de alta el contacto desde la cita. Con más de tres palabras el nombre
 * se queda con las de delante («María José Pérez Gómez»): los dos apellidos
 * son siempre los dos últimos.
 */
export function fichaDesdeNombre(texto) {
  const t = String(texto || '').trim().split(/\s+/).filter(Boolean);
  if (t.length === 0) return { name: '', surname1: '', surname2: '' };
  if (t.length === 1) return { name: t[0], surname1: '', surname2: '' };
  if (t.length === 2) return { name: t[0], surname1: t[1], surname2: '' };
  return { name: t.slice(0, -2).join(' '), surname1: t[t.length - 2], surname2: t[t.length - 1] };
}

/** Cuáles de los cuatro faltan en una ficha. */
export function faltanDe(form) {
  return CAMPOS_ALTA.filter(([clave]) => vacio(form[clave])).map(([, , etiqueta]) => etiqueta);
}

/**
 * Campo obligatorio del alta: asterisco y borde rojo al salirse vacío.
 * Fuera del componente a propósito: declarado dentro, React lo desmontaría
 * en cada tecla y el cursor se saldría del campo al escribir.
 */
function CampoAlta({ clave, etiqueta, valor, obligatorio, tocado, onChange, onBlur }) {
  const mal = obligatorio && vacio(valor) && tocado;
  return (
    <div>
      <Label htmlFor={`patient-${clave}`}>
        {etiqueta}
        {obligatorio && <span className="text-red-600"> *</span>}
      </Label>
      <Input
        id={`patient-${clave}`}
        value={valor || ''}
        onChange={(e) => onChange(clave, e.target.value)}
        onBlur={() => onBlur(clave)}
        required={obligatorio}
        aria-required={obligatorio || undefined}
        aria-invalid={mal}
        className={mal ? 'border-red-300' : ''}
        data-testid={`patient-${clave}-input`}
      />
      {mal && (
        <div className="text-xs text-red-600 mt-1" data-testid={`patient-${clave}-error`}>
          Obligatorio
        </div>
      )}
    </div>
  );
}

/**
 * Lo que el alta ha heredado de lo dicho al citarle en Leads (16/10/2026:
 * la fecha de nacimiento y el sexo, si el lead es la misma persona). Lo
 * decide el servidor (`heredado_del_lead`); aquí se dice.
 */
export function textoHeredado(heredado) {
  const campos = heredado?.campos || {};
  const partes = [];
  if (campos.birth_date) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(campos.birth_date);
    partes.push(`fecha de nacimiento ${m ? `${m[3]}/${m[2]}/${m[1]}` : campos.birth_date}`);
  }
  if (campos.sexo) partes.push(`sexo ${campos.sexo}`);
  return partes.length ? `Con lo dicho al citarle en Leads: ${partes.join(' y ')}.` : '';
}

/**
 * El sexo (16/10/2026): una de las cuatro opciones o sin dato. «No consta» es
 * una respuesta (se preguntó y no se sabe). Lo de antes que no es ninguna
 * («F», «M»…) se enseña tal cual, como dato antiguo, hasta que se elija una:
 * así no se cambia sin querer al guardar otra cosa.
 */
function CampoSexo({ valor, onChange }) {
  const canonico = sexoCanonico(valor);
  const antiguo = canonico === null ? String(valor) : '';
  return (
    <div>
      <Label htmlFor="patient-sexo">Sexo</Label>
      <select id="patient-sexo" value={antiguo ? `antiguo:${antiguo}` : (canonico || '')}
        onChange={(e) => { if (!e.target.value.startsWith('antiguo:')) onChange(e.target.value); }}
        data-testid="patient-sexo-input"
        className="mt-1 block w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
        <option value="">Sin dato</option>
        {antiguo ? <option value={`antiguo:${antiguo}`}>{`«${antiguo}» (dato antiguo)`}</option> : null}
        {OPCIONES_SEXO.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

export default function PatientFormDialog({ open, onOpenChange, onSaved, initial }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => (initial ? conOrigen(initial) : {
    name: '', surname1: '', surname2: '', nif: '', birth_date: '', sexo: '', email: '', phone: '',
    address: { calle: '', poblacion: '', provincia: '', codigo_postal: '' },
    profession: '', how_known_us: '', notes: ''
  }));
  // «Tocados»: el rojo de un campo obligatorio vacío solo sale tras salir de
  // él o al intentar guardar, no nada más abrir el formulario (mismo criterio
  // que los detalles obligatorios de la cita).
  const [tocados, setTocados] = useState({});
  // Por qué no se ha guardado (el 409 de «otra persona ha cambiado esta
  // ficha»), EN la ventana: un aviso de cuatro segundos no se lee.
  const [errorServidor, setErrorServidor] = useState('');
  // LA FICHA QUE SE TIENE DELANTE (23/9/2026). La página le pasa la ficha que
  // cargó al entrar, que puede ser de hace una hora: al abrir la ventana se
  // vuelve a leer, y ESA es la base de lo que se compara al guardar. Así un
  // 409 de «otra persona ha cambiado esta ficha» se arregla cerrando y
  // abriendo, o con «Recargar la ficha», sin salir de la página.
  const [abierta, setAbierta] = useState(initial);
  // ¿Ha escrito ya algo? Si la ficha fresca llega después, no se le pisa lo
  // tecleado: se queda la base que tenía a la vista cuando empezó.
  const editadoRef = useRef(false);

  const leerLaFicha = useCallback((forzar = false) => {
    if (!initial?.id) return () => {};
    let vivo = true;
    api.get(`/api/patients/${initial.id}`)
      .then((r) => {
        const fresca = r?.data;
        if (!vivo || !fresca || fresca.id !== initial.id) return;
        if (editadoRef.current && !forzar) return;
        editadoRef.current = false;
        setAbierta(fresca);
        setForm(conOrigen(fresca));
        if (forzar) setErrorServidor('');
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [initial]);

  useEffect(() => {
    if (!open) return undefined;
    setTocados({});
    setErrorServidor('');
    editadoRef.current = false;
    setAbierta(initial);
    setForm(initial ? conOrigen(initial) : {
      name: '', surname1: '', surname2: '', nif: '', birth_date: '', sexo: '', email: '', phone: '',
      address: { calle: '', poblacion: '', provincia: '', codigo_postal: '' },
      profession: '', how_known_us: '', notes: ''
    });
    return leerLaFicha();
  }, [open, initial, leerLaFicha]);

  const setField = (k, v) => { editadoRef.current = true; setForm(f => ({ ...f, [k]: v })); };
  const setAddr = (k, v) => {
    editadoRef.current = true;
    setForm(f => ({ ...f, address: { ...(f.address || {}), [k]: v } }));
  };
  const tocar = (k) => setTocados(t => ({ ...t, [k]: true }));

  // Al EDITAR solo se exige el NOMBRE. Los dos apellidos y el teléfono son
  // del ALTA: hay fichas antiguas sin segundo apellido ni teléfono y
  // bloquearlas dejaría sin poder corregirles un email o una dirección, u
  // obligaría a inventarse los datos que faltan. El nombre es otra cosa: sin
  // él la ficha no se puede ni buscar, y al guardar se propaga a las citas y
  // a las sesiones de quirófano, así que vaciarlo vacía también las agendas.
  // Lo que le falta a una ficha antigua y NO bloquea (todo menos el nombre).
  const faltanOpcionales = useMemo(
    () => faltanDe(form).filter((etiqueta) => !ETIQUETAS_SIEMPRE.includes(etiqueta)),
    [form],
  );
  // El email de una ficha ANTIGUA no bloquea: solo se exige que parezca un
  // correo si se está escribiendo uno nuevo. Si no, una ficha con «ana@» de
  // hace años no se podía guardar ni para corregirle el teléfono.
  const emailOriginal = String((abierta && abierta.email) || '').trim();
  const emailMal = !emailValido(form.email)
    && (!isEdit || String(form.email || '').trim() !== emailOriginal);
  // El rojo del campo espera a salir de él (como los obligatorios); el aviso
  // de abajo sale ya, porque es el que explica el botón apagado.
  const emailRojo = emailMal && !!tocados.email;
  // Qué impide guardar, y por qué. Al editar solo el nombre; al crear, los
  // cuatro. El email, en los dos casos, si se ha escrito algo que no lo es.
  // Y «cómo nos conoció» en los DOS (decisión de dirección, 23/9/2026): al dar
  // de alta y al editar la ficha. En una ficha antigua sin origen, quien la
  // edita lo elige —o «No lo recuerda»— y guarda; el servidor lo exige igual.
  const aviso = (isEdit
    ? (vacio(form.name) ? AVISO_NOMBRE_VACIO : '')
    : avisoAltaIncompleta(form)
  ) || (faltaOrigen(form.how_known_us) ? AVISO_FALTA_ORIGEN : '')
    || (emailMal ? AVISO_EMAIL : '');
  const incompleto = !!aviso;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (incompleto) {
      setTocados({
        ...Object.fromEntries(CAMPOS_ALTA.map(([k]) => [k, true])),
        how_known_us: true,
      });
      toast.error(aviso);
      return;
    }
    setErrorServidor('');
    try {
      if (isEdit) {
        const { cambios, valoresAlAbrir } = cambiosDeLaVentana(abierta, form);
        if (!Object.keys(cambios).length) {
          toast.success('No había nada que cambiar');
          onOpenChange(false);
          return;
        }
        const res = await api.put(`/api/patients/${initial.id}`, {
          ...cambios,
          valores_al_abrir: valoresAlAbrir,
          updated_at_al_abrir: abierta?.updated_at ?? null,
        });
        toast.success('Paciente actualizado');
        onSaved && onSaved(res.data);
      } else {
        const res = await api.post('/api/patients', form);
        // Lo que ha heredado de lo dicho al citarle en Leads (16/10/2026).
        const heredado = textoHeredado(res.data?.heredado_del_lead);
        toast.success(heredado ? `Paciente creado. ${heredado}` : 'Paciente creado');
        onSaved && onSaved(res.data);
      }
      onOpenChange(false);
    } catch (err) {
      const detalle = err?.response?.data?.detail;
      const texto = typeof detalle === 'string' ? detalle : 'Error';
      if (err?.response?.status === 409) setErrorServidor(texto);
      toast.error(texto);
    }
  };

  const campo = (clave) => {
    const [, etiqueta] = CAMPOS_ALTA.find(([k]) => k === clave);
    return (
      <CampoAlta
        clave={clave}
        etiqueta={etiqueta}
        valor={form[clave]}
        obligatorio={!isEdit || SIEMPRE_OBLIGATORIO.includes(clave)}
        tocado={!!tocados[clave]}
        onChange={setField}
        onBlur={tocar}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden p-0 flex flex-col gap-0">
        <DialogHeader className="px-5 py-3.5 border-b border-border shrink-0">
          <DialogTitle>{isEdit ? 'Editar paciente' : 'Nuevo paciente'}</DialogTitle>
        </DialogHeader>
        {/* noValidate: los avisos los damos nosotros, en cristiano y todos
            juntos, en vez de los globos del navegador campo a campo. Como el
            navegador entonces NO comprueba nada, todo lo que antes hacía
            `required`/`type=email` tiene que estar arriba en `aviso`. */}
        <form onSubmit={onSubmit} noValidate className="flex flex-col min-h-0 flex-1">
          <div className="overflow-y-auto px-5 py-4 space-y-3 flex-1">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {campo('name')}
            {campo('surname1')}
            {campo('surname2')}
            <div><Label>NIF</Label><Input value={form.nif || ''} onChange={(e) => setField('nif', e.target.value)} data-testid="patient-nif-input" /></div>
            <div><Label>Fecha de nacimiento</Label><Input type="date" value={form.birth_date || ''} onChange={(e) => setField('birth_date', e.target.value)} data-testid="patient-birth-date-input" /></div>
            <CampoSexo valor={form.sexo} onChange={(v) => setField('sexo', v)} />
            {campo('phone')}
            {/* El email no es obligatorio, pero si se escribe se comprueba:
                el formulario no valida por su cuenta (noValidate) y «ana@»
                se estaba guardando tal cual. */}
            <div className="md:col-span-2">
              <Label htmlFor="patient-email">Email</Label>
              <Input
                id="patient-email"
                type="email"
                value={form.email || ''}
                onChange={(e) => setField('email', e.target.value)}
                onBlur={() => tocar('email')}
                aria-invalid={emailRojo}
                className={emailRojo ? 'border-red-300' : ''}
                data-testid="patient-email-input"
              />
              {emailRojo && (
                <div className="text-xs text-red-600 mt-1" data-testid="patient-email-error">{AVISO_EMAIL}</div>
              )}
            </div>
            <div><Label>Profesión</Label><Input value={form.profession || ''} onChange={(e) => setField('profession', e.target.value)} /></div>
            {/* Obligatorio (23/9/2026) y arriba, con los datos que se piden
                siempre: antes era texto libre al final de la dirección y
                casi nadie lo rellenaba. */}
            <div className="md:col-span-2">
              <CampoComoNosConocio
                valor={form.how_known_us}
                onChange={(v) => setField('how_known_us', v)}
                onBlur={() => tocar('how_known_us')}
                tocado={!!tocados.how_known_us}
              />
            </div>
          </div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold pt-2">Dirección</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2"><Label>Calle</Label><Input value={form.address?.calle || ''} onChange={(e) => setAddr('calle', e.target.value)} data-testid="patient-address-calle" /></div>
            <div><Label>Población</Label><Input value={form.address?.poblacion || ''} onChange={(e) => setAddr('poblacion', e.target.value)} /></div>
            <div><Label>Provincia</Label><Input value={form.address?.provincia || ''} onChange={(e) => setAddr('provincia', e.target.value)} /></div>
            <div><Label>Código postal</Label><Input value={form.address?.codigo_postal || ''} onChange={(e) => setAddr('codigo_postal', e.target.value)} /></div>
          </div>
          {/* Qué falta, a la vista y en cristiano: el botón de guardar está
              apagado y sin esto no se sabe por qué. Al editar una ficha
              antigua incompleta solo se avisa: se guarda igual (menos el
              nombre, que no se puede dejar en blanco). */}
          {incompleto && (
            <div className="text-xs text-red-600" data-testid="patient-faltan">{aviso}</div>
          )}
          {errorServidor && (
            <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 space-y-2" data-testid="patient-conflicto">
              <div data-testid="patient-conflicto-texto">{errorServidor}</div>
              {isEdit && (
                <Button type="button" size="sm" variant="outline" onClick={() => leerLaFicha(true)} data-testid="patient-recargar">
                  Recargar la ficha
                </Button>
              )}
            </div>
          )}
          {isEdit && faltanOpcionales.length > 0 && (
            <div className="text-xs text-muted-foreground" data-testid="patient-ficha-antigua">
              {`A esta ficha antigua le falta ${enumerar(faltanOpcionales)}. Complétalo si lo sabes; se guarda igual.`}
            </div>
          )}
          </div>
          <DialogFooter className="px-5 py-3 border-t border-border shrink-0 bg-white gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={incompleto} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="patient-save-button">{isEdit ? 'Guardar' : 'Crear paciente'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
